import QRCode from "qrcode-svg";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getRfidRowId } from "./rfidHistoryUtils";

/** Stable selection / QR payload key for an RFID row. */
export const getRfidQrKey = (row) => {
  const code = row?.rfidCode || row?.assetRfidCode;
  if (code && code !== "-" && code !== "N/A") return String(code);
  const id = getRfidRowId(row);
  return id == null ? "" : String(id);
};

/** Normalize API / mapped row fields used on QR modal & PDF. */
export const mapRfidToQrData = (row) => {
  const rfidCode = row?.rfidCode || row?.assetRfidCode || "-";
  const zoneName = row?.zoneName || "-";
  const wardName =
    row?.wardName || row?.wardNo || row?.circleName || "-";
  const assetTypeId = String(row?.rfidTypeId ?? row?.assetTypeId ?? "");
  const showVehicleNo = VEHICLE_NO_ASSET_TYPE_IDS.includes(assetTypeId);
  const vehicleNo = row?.otherNo || row?.vehicleNo || "-";

  return {
    rfidCode: rfidCode === "N/A" ? "-" : rfidCode,
    zoneName: zoneName === "N/A" ? "-" : zoneName,
    wardName: wardName === "N/A" ? "-" : wardName,
    showVehicleNo,
    vehicleNo: vehicleNo === "N/A" ? "-" : vehicleNo,
    qrContent: getRfidQrKey(row),
  };
};

/** Asset types whose QR label also shows the vehicle number. */
const VEHICLE_NO_ASSET_TYPE_IDS = ["1", "3"];

/** Label/value rows printed below the QR (modal + PDF). */
export const getRfidQrLabelRows = (qrData) => {
  const rows = [
    { label: "ASSET CODE", value: qrData?.rfidCode || "-" },
    { label: "ZONE", value: qrData?.zoneName || "-" },
    { label: "WARD", value: qrData?.wardName || "-" },
  ];
  if (qrData?.showVehicleNo) {
    rows.push({ label: "VEHICLE NO", value: qrData?.vehicleNo || "-" });
  }
  return rows;
};

export const generateQRCodeSVG = (content) => {
  try {
    const qr = new QRCode({
      content: content || "",
      width: 400,
      height: 400,
      color: "#000000",
    });
    return qr.svg();
  } catch (error) {
    console.error("Error generating QR code:", error);
    return "<svg></svg>";
  }
};

/**
 * Full A4 page: large QR near the top (little empty space) + labels below.
 * Entire QR stays visible — not vertically centered (avoids bottom clipping).
 */
const buildRfidQrPdf = (imgData, qrData) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  // Place QR near top so full QR is visible (no large empty gap above)
  const topMargin = 8;
  const qrWidthMM = 65;
  const qrX = (pageWidth - qrWidthMM) / 2;
  const qrY = topMargin;
  pdf.addImage(imgData, "JPEG", qrX, qrY, qrWidthMM, qrWidthMM);

  const labelRows = getRfidQrLabelRows(qrData);
  const labels = labelRows.map((row) => row.label);
  const values = labelRows.map((row) => row.value);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(12);
  pdf.setTextColor(0, 0, 0);

  let maxLabelWidth = 0;
  labels.forEach((label) => {
    const labelWidth = pdf.getTextWidth(label);
    if (labelWidth > maxLabelWidth) maxLabelWidth = labelWidth;
  });

  const lineHeight = 8;
  const blockWidth = maxLabelWidth + 50;
  const textX = (pageWidth - blockWidth) / 2;
  let textY = qrY + qrWidthMM + 12;

  labels.forEach((label, i) => {
    pdf.text(label, textX, textY);
    pdf.text(":", textX + maxLabelWidth + 2, textY);
    pdf.text(String(values[i] || "-"), textX + maxLabelWidth + 8, textY);
    textY += lineHeight;
  });

  return pdf;
};

/** QR is generated at 512px, then scaled 2× onto a 1024px canvas for the PDF. */
const renderQrToJpegDataUrl = (content, qrSize = 512, scale = 2) =>
  new Promise((resolve, reject) => {
    const qr = new QRCode({
      content: content || "",
      padding: 4,
      width: qrSize,
      height: qrSize,
      color: "#000000",
      background: "#ffffff",
      ecl: "M",
    });

    const svg = qr.svg();
    const canvas = document.createElement("canvas");
    const img = new Image();

    img.onload = () => {
      const canvasSize = qrSize * scale;
      canvas.width = canvasSize;
      canvas.height = canvasSize;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, canvasSize, canvasSize);
      resolve(canvas.toDataURL("image/jpeg", 1.0));
    };

    img.onerror = reject;
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

/** Single RFID QR → full A4 PDF. */
export const downloadQRCodeImage = async (qrData) => {
  try {
    const content = qrData?.qrContent || qrData?.rfidCode || "";
    if (!content) return;

    const imgData = await renderQrToJpegDataUrl(content);
    const pdf = buildRfidQrPdf(imgData, qrData);
    const fileName = (qrData?.rfidCode || "rfid").replace(/[\\/:*?"<>|]/g, "-");
    pdf.save(`qr-code-${fileName}.pdf`);
  } catch (error) {
    console.error("Error generating RFID QR PDF:", error);
  }
};

/** Bulk RFID QR → ZIP of full A4 PDFs. */
export const handleDownloadQRCode = async (items, onProgress) => {
  const zip = new JSZip();
  const limitedItems = items.slice(0, 5000);
  const chunkSize = 10;
  const totalItems = limitedItems.length;

  for (let i = 0; i < totalItems; i += chunkSize) {
    const chunk = limitedItems.slice(i, i + chunkSize);

    await Promise.all(
      chunk.map(async (item, chunkIndex) => {
        const qrData = item?.rfidCode != null ? item : mapRfidToQrData(item);
        const content = qrData?.qrContent || qrData?.rfidCode || "";
        const imgData = await renderQrToJpegDataUrl(content);
        const pdf = buildRfidQrPdf(imgData, qrData);
        const pdfBlob = pdf.output("blob");
        const globalIndex = i + chunkIndex;
        const fileName = (qrData.rfidCode || "unknown").replace(
          /[\\/:*?"<>|]/g,
          "-"
        );
        zip.file(`${globalIndex + 1}_${fileName}.pdf`, pdfBlob);
      })
    );

    const processedCount = Math.min(i + chunkSize, totalItems);
    const progress = Math.round((processedCount / totalItems) * 100);
    if (onProgress) {
      onProgress(progress, processedCount, totalItems);
    }

    await new Promise((resolve) => setTimeout(resolve, 10));
  }

  try {
    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 9 },
    });
    saveAs(content, "qrcodes.zip");
  } catch (error) {
    console.error("Error generating or downloading ZIP file:", error);
  }
};
